import { useEffect, useState } from 'react';
import {
  ACCOUNT_CONNECTION_STATUSES,
  type AccountConnection,
  type AccountConnectionStatus,
  type AccountLookup,
  type TestAccountLookupResponse,
} from '@stonelink/shared';
import { api } from '../api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Textarea,
  formatDate,
} from '../ui';

const STATUS_LABEL: Record<AccountConnectionStatus, string> = {
  disabled: 'Off — the assistant never looks anything up',
  testing: 'Testing — only this page can run lookups',
  live: 'Live — signed-in visitors can ask about their account',
};

const STATUS_TONE = {
  ok: 'good',
  denied: 'warn',
  error: 'warn',
  timeout: 'warn',
} as const;

/**
 * Connect the assistant to the business's own backend, so a signed-in visitor
 * can ask about their own orders, bookings or subscription.
 *
 * The page is deliberately built around the two things an owner actually needs:
 * seeing exactly which lookups we discovered (and why any were refused), and
 * being able to run one by hand when it is not working.
 */
export function AccountDataPage() {
  const [connection, setConnection] = useState<AccountConnection | null>(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [outboundSecret, setOutboundSecret] = useState('');
  const [lookups, setLookups] = useState<AccountLookup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Shown exactly once, right after minting. The API will not return it again. */
  const [freshSecret, setFreshSecret] = useState<string | null>(null);

  const [testAction, setTestAction] = useState('');
  const [testSubject, setTestSubject] = useState('');
  const [testParams, setTestParams] = useState('{}');
  const [testResult, setTestResult] = useState<TestAccountLookupResponse | null>(null);

  const load = async () => {
    const [loaded, recent] = await Promise.all([
      api.getAccountConnection(),
      api.listAccountLookups(),
    ]);
    setConnection(loaded);
    setBaseUrl(loaded.baseUrl ?? '');
    setLookups(recent);
  };

  useEffect(() => {
    void load().catch(() => setError('Could not load your account connection.'));
  }, []);

  // Keeps the test form pointed at a lookup that still exists after a refresh.
  useEffect(() => {
    const actions = connection?.actions ?? [];
    if (actions.length > 0 && !actions.some((action) => action.name === testAction)) {
      setTestAction(actions[0]!.name);
    }
  }, [connection, testAction]);

  /** Wraps an action so every button reports its own failure the same way. */
  const run = async (work: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : fallback);
    } finally {
      setBusy(false);
    }
  };

  const saveConnection = (patch: Parameters<typeof api.updateAccountConnection>[0]) =>
    run(async () => {
      setConnection(await api.updateAccountConnection(patch));
    }, 'Could not save the connection.');

  const rotateSecret = () =>
    run(async () => {
      const { identitySecret } = await api.rotateIdentitySecret();
      setFreshSecret(identitySecret);
      await load();
    }, 'Could not generate a signing secret.');

  const refresh = () =>
    run(async () => {
      setConnection(await api.refreshManifest());
    }, 'Could not read the manifest.');

  const runTest = () =>
    run(async () => {
      let params: Record<string, unknown>;
      try {
        params = JSON.parse(testParams || '{}') as Record<string, unknown>;
      } catch {
        throw new Error('Parameters must be valid JSON.');
      }
      setTestResult(
        await api.testAccountLookup({ action: testAction, subject: testSubject, params }),
      );
      setLookups(await api.listAccountLookups());
    }, 'The test lookup failed.');

  if (!connection) {
    return (
      <div>
        <PageHeader
          title="Account data"
          description="Let signed-in visitors ask about their own account."
        />
        {error ? <ErrorNote>{error}</ErrorNote> : <p className="text-sm text-muted">Loading…</p>}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      <PageHeader
        title="Account data"
        description="Connect your own backend so signed-in visitors can ask about their orders, bookings or subscription."
      />

      {error && <ErrorNote>{error}</ErrorNote>}

      <Card
        title="Your backend"
        description="One HTTPS address on your own site. We call it; it never calls us."
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="base-url">Backend URL</Label>
            <Input
              id="base-url"
              value={baseUrl}
              placeholder="https://example.com/assist"
              onChange={(event) => setBaseUrl(event.target.value)}
            />
            <p className="mt-1.5 text-sm text-muted">
              We will call <code>{baseUrl || 'https://example.com/assist'}/manifest</code> and{' '}
              <code>/actions/&lt;name&gt;</code>.
            </p>
          </div>

          <div>
            <Label htmlFor="outbound-secret">Secret we send you</Label>
            <Input
              id="outbound-secret"
              type="password"
              value={outboundSecret}
              placeholder={
                connection.hasOutboundSecret ? 'Set — type a new one to replace it' : 'Not set'
              }
              onChange={(event) => setOutboundSecret(event.target.value)}
            />
            <p className="mt-1.5 text-sm text-muted">
              Sent as a bearer token so your backend can tell our requests from anyone else’s.
            </p>
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              value={connection.status}
              onChange={(event) =>
                void saveConnection({ status: event.target.value as AccountConnectionStatus })
              }
              className="h-11 w-full rounded-xl border border-line bg-white/5 px-3 text-sm text-ink outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/25"
            >
              {ACCOUNT_CONNECTION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABEL[status]}
                </option>
              ))}
            </select>
          </div>

          <Button
            disabled={busy}
            onClick={() =>
              void saveConnection({
                baseUrl: baseUrl.trim() || null,
                ...(outboundSecret.trim() ? { outboundSecret: outboundSecret.trim() } : {}),
              }).then(() => setOutboundSecret(''))
            }
          >
            Save
          </Button>
        </div>
      </Card>

      <Card
        title="Signing secret"
        description="Your site signs a short-lived token for whoever is logged in; we verify it with this."
      >
        {freshSecret ? (
          <div className="space-y-3">
            <p className="rounded-xl border border-amber-300/30 bg-amber-300/10 px-3 py-2 text-sm text-amber-200">
              Copy this now — it is not shown again.
            </p>
            <code className="block break-all rounded-xl border border-line bg-white/5 px-3 py-2 font-mono text-xs">
              {freshSecret}
            </code>
            <Button
              variant="secondary"
              onClick={() => void navigator.clipboard.writeText(freshSecret)}
            >
              Copy
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Badge tone={connection.hasIdentitySecret ? 'good' : 'neutral'}>
              {connection.hasIdentitySecret ? 'Set' : 'Not set'}
            </Badge>
            <Button variant="secondary" disabled={busy} onClick={() => void rotateSecret()}>
              {connection.hasIdentitySecret ? 'Replace secret' : 'Generate secret'}
            </Button>
          </div>
        )}
        <p className="mt-4 text-sm text-muted">
          Replacing the secret signs everyone out of the assistant until your site uses the new one.
        </p>
      </Card>

      <Card
        title="Lookups we found"
        description="Read from your manifest. Change it on your side, then refresh here."
        actions={
          <Button variant="secondary" disabled={busy} onClick={() => void refresh()}>
            Refresh
          </Button>
        }
      >
        {connection.manifestError && <ErrorNote>{connection.manifestError}</ErrorNote>}

        {connection.actions.length === 0 ? (
          <EmptyState
            title="No lookups yet"
            hint="Publish a manifest at your backend URL, then press Refresh."
          />
        ) : (
          <ul className="space-y-3">
            {connection.actions.map((action) => (
              <li key={action.name} className="rounded-lg border border-line px-4 py-3">
                <p className="font-mono text-sm font-medium">{action.name}</p>
                <p className="mt-0.5 text-sm text-muted">{action.description}</p>
                <p className="mt-1 text-xs text-muted">
                  Parameters: {Object.keys(action.parameters.properties).join(', ') || 'none'}
                </p>
              </li>
            ))}
          </ul>
        )}

        {connection.rejectedActions.length > 0 && (
          <div className="mt-5">
            {/* Never dropped silently: a vanished action reads as "the bot is broken". */}
            <p className="mb-2 text-sm font-medium">Not offered to the assistant</p>
            <ul className="space-y-2">
              {connection.rejectedActions.map((rejected, index) => (
                <li
                  key={`${rejected.name}-${index}`}
                  className="rounded-xl border border-amber-300/30 bg-amber-300/10 px-3 py-2 text-sm text-amber-200"
                >
                  <span className="font-mono">{rejected.name}</span> — {rejected.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {connection.manifestFetchedAt && (
          <p className="mt-4 text-xs text-muted">
            Last read {formatDate(connection.manifestFetchedAt)}
          </p>
        )}
      </Card>

      <Card
        title="Try a lookup"
        description="Runs exactly what the assistant would run, against a customer of your choosing."
      >
        {connection.actions.length === 0 ? (
          <EmptyState title="Nothing to test yet" hint="Refresh your manifest first." />
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="test-action">Lookup</Label>
              <select
                id="test-action"
                value={testAction}
                onChange={(event) => setTestAction(event.target.value)}
                className="h-11 w-full rounded-xl border border-line bg-white/5 px-3 text-sm text-ink outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/25"
              >
                {connection.actions.map((action) => (
                  <option key={action.name} value={action.name}>
                    {action.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="test-subject">Customer id</Label>
              <Input
                id="test-subject"
                value={testSubject}
                placeholder="The id your site puts in the token’s `sub`"
                onChange={(event) => setTestSubject(event.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="test-params">Parameters (JSON)</Label>
              <Textarea
                id="test-params"
                rows={3}
                value={testParams}
                onChange={(event) => setTestParams(event.target.value)}
                className="font-mono"
              />
            </div>

            <Button disabled={busy || !testSubject.trim()} onClick={() => void runTest()}>
              Run lookup
            </Button>

            {testResult && (
              <div className="rounded-xl border border-line bg-white/5 p-4">
                <div className="flex items-center gap-2">
                  <Badge tone={STATUS_TONE[testResult.status]}>{testResult.status}</Badge>
                  <span className="text-sm text-muted">
                    {testResult.durationMs} ms
                    {testResult.httpStatus !== null && ` · HTTP ${testResult.httpStatus}`}
                  </span>
                </div>
                {testResult.error && (
                  <p className="mt-2 text-sm text-red-300">{testResult.error}</p>
                )}
                {testResult.data !== undefined && (
                  <pre className="mt-3 overflow-x-auto rounded-xl border border-line bg-white/5 p-3 font-mono text-xs">
                    {JSON.stringify(testResult.data, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card
        title="Recent lookups"
        description="Every account lookup, including the ones from this page."
      >
        {lookups.length === 0 ? (
          <EmptyState
            title="Nothing yet"
            hint="Lookups appear here as visitors ask about their accounts."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="pb-2 pr-4 font-medium">When</th>
                  <th className="pb-2 pr-4 font-medium">Lookup</th>
                  <th className="pb-2 pr-4 font-medium">Customer</th>
                  <th className="pb-2 pr-4 font-medium">Result</th>
                  <th className="pb-2 font-medium">Took</th>
                </tr>
              </thead>
              <tbody>
                {lookups.map((lookup) => (
                  <tr key={lookup.id} className="border-t border-line">
                    <td className="py-2 pr-4 whitespace-nowrap text-muted">
                      {formatDate(lookup.createdAt)}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">{lookup.action}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{lookup.endUserRef}</td>
                    <td className="py-2 pr-4">
                      <Badge tone={STATUS_TONE[lookup.status]}>{lookup.status}</Badge>
                    </td>
                    <td className="py-2 whitespace-nowrap text-muted">{lookup.durationMs} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
